/**
 * When a session the server ended may show its ending (storybooks phase 2
 * §2.6): once the tutor's goodbye has finished playing, not when the news
 * arrives.
 *
 * `SessionEnded` rides the GraphQL bus and the goodbye rides the audio
 * socket, so the two can land in either order. The server writes the
 * goodbye's last frame before it ends the session (`handle_utterance`), so
 * when the event arrives the audio is on the playback queue or already
 * played: `silent` says which, and a queue still playing reports the end
 * through `drained`.
 */
export class ServerEnd {
  private announced = false;
  private shown = false;

  /** `SessionEnded` arrived. `true` means show the ending now. */
  announce(silent: boolean): boolean {
    if (this.announced) return false;
    this.announced = true;
    return silent && this.show();
  }

  /** The playback queue ran dry. `true` means show the ending now. */
  drained(): boolean {
    return this.announced && this.show();
  }

  private show(): boolean {
    if (this.shown) return false;
    this.shown = true;
    return true;
  }
}

/**
 * Whether the session view acts on an event, given whether `SessionEnded`
 * has already arrived.
 *
 * An utterance the learner opened over the goodbye reaches the server after
 * the session has ended, and `handle_utterance` answers it with
 * `SessionFailed` and then `Idle`; a silence re-prompt ends in `Idle`. Shown,
 * the first puts an error line above a child's celebration for a talk that
 * went exactly as planned, and the second moves the face off the goodbye.
 */
export const heeded = (typename: string, serverEnded: boolean): boolean =>
  !serverEnded || (typename !== 'SessionFailed' && typename !== 'StateChanged');
